import {useCallback, useEffect, useMemo, useRef, useState} from 'react';
import {useDisclosure, useToast} from '@chakra-ui/react';
import {patientService} from '../../services/patientService';
import {ehrService} from '../../services/ehrService';
import {isDataLayerNotFound} from '../../services/dataLayerClient';
import {useUser} from '../../contexts/UserContext';
import {dateInputToLocalDate} from '../../constants/designTokens';

const clean = (value: any) => String(value ?? '').trim();

const dateInputValue = (value: any) => {
  if (!value) return '';
  const date = value.toDate ? value.toDate() : new Date(value);
  if (Number.isNaN(date.getTime())) return '';
  return date.toISOString().split('T')[0];
};

const displayName = (firstName: any, lastName: any) =>
  [firstName, lastName].map(clean).filter(Boolean).join(' ');

export const normalizePatientCrudPatient = (patient: any) => {
  if (!patient) return null;
  const localId = patient._localPatientId || patient.localId || '';
  const ehrId = patient._ehrPatientId || patient.ehrId || '';
  const id =
    patient.id ||
    (localId ? `local_${localId}` : ehrId ? `ehr_${ehrId}` : null);

  return {
    ...patient,
    id: localId || patient.id || ehrId || null,
    first_name: patient.first_name || patient.firstName || '',
    last_name: patient.last_name || patient.lastName || '',
    preferred_language: patient.preferred_language || patient.language || '',
    preferred_dialect: patient.preferred_dialect || patient.dialect || '',
    date_of_birth: patient.date_of_birth || patient.dob || null,
    mrn: patient.mrn || '',
    gender: patient.gender || '',
    notes: patient.notes || '',
    phone_number: patient.phone_number || patient.phone || '',
    email: patient.email || '',
    _hasLocalRecord:
      patient._hasLocalRecord ?? Boolean(localId || (!ehrId && patient.id)),
    _localPatientId: localId || patient._localPatientId || null,
    _linkedEhrId: patient._linkedEhrId || patient.linkedEhrId || null,
    _isEhrOnly: patient._isEhrOnly ?? Boolean(!localId && ehrId),
    _ehrPatientId: ehrId || null,
    _matchKind:
      patient._matchKind ||
      patient.matchKind ||
      (localId && !ehrId ? 'localOnly' : ''),
    _plannerId: id,
  };
};

export const patientCanUseLocalCrud = (patientInput: any) => {
  const patient = normalizePatientCrudPatient(patientInput);
  return Boolean(
    patient?._hasLocalRecord &&
    !patient._isEhrOnly &&
    !patient._ehrPatientId &&
    !patient._linkedEhrId &&
    (!patient._matchKind || patient._matchKind === 'localOnly'),
  );
};

const formDataFromPatient = (patient: any) => ({
  firstName: patient?.first_name || '',
  lastName: patient?.last_name || '',
  preferredLanguage: patient?.preferred_language || '',
  preferredDialect: patient?.preferred_dialect || '',
  mrn: patient?.mrn || '',
  notes: patient?.notes || '',
  gender: patient?.gender || '',
  phoneNumber: patient?.phone_number || '',
  email: patient?.email || '',
  dateOfBirth: dateInputValue(patient?.date_of_birth),
});

const updatePayloadFromForm = (formData: any) => ({
  first_name: formData.firstName?.trim(),
  last_name: formData.lastName?.trim(),
  preferred_language: formData.preferredLanguage || null,
  preferred_dialect: formData.preferredDialect || null,
  mrn: formData.mrn?.trim() || null,
  notes: formData.notes?.trim() || null,
  gender: formData.gender || null,
  phone_number: formData.phoneNumber?.trim() || null,
  email: formData.email?.trim() || null,
  date_of_birth: dateInputToLocalDate(formData.dateOfBirth),
});

export const mergePlannerPatientUpdate = (
  patient: any,
  updates: Record<string, any> = {},
) => {
  if (!patient) return patient;
  const firstName =
    updates.first_name ?? patient.firstName ?? patient.first_name ?? '';
  const lastName =
    updates.last_name ?? patient.lastName ?? patient.last_name ?? '';
  const fullName =
    displayName(firstName, lastName) ||
    patient.fullName ||
    patient.displayName ||
    '';

  return {
    ...patient,
    firstName,
    lastName,
    fullName,
    displayName: fullName,
    language: updates.preferred_language ?? patient.language ?? '',
    dialect: updates.preferred_dialect ?? patient.dialect ?? '',
    mrn: updates.mrn ?? patient.mrn ?? '',
    dob: updates.date_of_birth ?? patient.dob ?? null,
    gender: updates.gender ?? patient.gender ?? '',
    phone: updates.phone_number ?? patient.phone ?? '',
    email: updates.email ?? patient.email ?? '',
    notes: updates.notes ?? patient.notes ?? '',
  };
};

export default function usePatientCrud({
  patient: patientInput,
  onUpdated,
  onDeleted,
}: Record<string, any> = {}) {
  const toast = useToast();
  const {userData} = useUser();
  const editModal = useDisclosure();
  const deleteDialog = useDisclosure();
  const cancelDeleteRef = useRef<any>(null);
  const [formData, setFormData] = useState<Record<string, any>>({});
  const [saving, setSaving] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [ehrConfig, setEhrConfig] = useState<any>(null);
  // `false` until we have a *definitive* answer about this company's EHR
  // status. Deletion gating fails closed on this flag (see below), so a
  // still-loading or failed config read keeps the destructive affordance
  // hidden instead of briefly exposing it to an EHR-linked account.
  const [ehrLoaded, setEhrLoaded] = useState(false);
  const patient = useMemo(
    () => normalizePatientCrudPatient(patientInput),
    [patientInput],
  );
  const localCrudEligible = useMemo(
    () => patientCanUseLocalCrud(patient),
    [patient],
  );

  useEffect(() => {
    if (!userData?.companyId) {
      // Company not resolved yet — status is unknown, so fail closed.
      setEhrConfig(null);
      setEhrLoaded(false);
      return undefined;
    }
    let active = true;
    setEhrLoaded(false);
    ehrService
      .fetchEhrConfig(userData.companyId)
      .then(config => {
        if (!active) return;
        setEhrConfig(config);
        // Only a successful read is a definitive answer. A rejected read
        // leaves `ehrLoaded` false on purpose so deletion stays disabled
        // rather than defaulting to "no EHR" and exposing the button.
        setEhrLoaded(true);
      })
      .catch(() => {
        if (!active) return;
        setEhrConfig(null);
      });
    return () => {
      active = false;
    };
  }, [userData?.companyId]);

  // Patient editing and deletion are both disabled entirely for EHR-linked
  // accounts — patient records are owned by the EHR, so clinicians must never
  // mutate or remove them here. Gate on a confirmed-inactive status (not just
  // "no active config") so the affordances stay hidden until we positively
  // know the company has no active EHR, avoiding the fail-open race while the
  // config loads.
  const ehrConfirmedInactive =
    ehrLoaded && !ehrService.isConfigActive(ehrConfig);
  const canEditPatient = localCrudEligible && ehrConfirmedInactive;
  const canDeletePatient = localCrudEligible && ehrConfirmedInactive;

  const openEditPatient = useCallback(() => {
    if (!patient) return;
    setFormData(formDataFromPatient(patient));
    editModal.onOpen();
  }, [editModal, patient]);

  const handleUpdatePatient = useCallback(async () => {
    // Only local-only patients are editable. EHR-owned or EHR-linked records
    // must never be written from here (their demographics are owned by the
    // EHR), even if a stale UI path reaches this handler.
    if (!patient?.id || !canEditPatient) return;
    setSaving(true);
    const updates = updatePayloadFromForm(formData);
    try {
      await patientService.updatePatient(patient.id, updates);
      editModal.onClose();
      onUpdated?.(updates, patient);
      toast({title: 'Patient updated', status: 'success', duration: 2000});
    } catch {
      toast({title: 'Failed to update', status: 'error'});
    } finally {
      setSaving(false);
    }
  }, [canEditPatient, editModal, formData, onUpdated, patient, toast]);

  const handleDeletePatient = useCallback(async () => {
    if (!patient?.id) return;
    // EHR-linked accounts can never delete patients, even if a stale UI path
    // somehow reaches this handler. `canDeletePatient` requires BOTH a
    // local-only patient (not EHR-owned/linked) AND a confirmed-inactive EHR
    // status — failing closed while the config loads — so neither an
    // EHR-linked patient nor an unknown EHR state can slip through.
    if (!canDeletePatient) {
      toast({
        title: 'Patient deletion is disabled for EHR-linked accounts',
        status: 'info',
        duration: 3000,
      });
      deleteDialog.onClose();
      return;
    }
    setDeleting(true);
    // Delete is idempotent: a 404 means the patient was already removed (e.g.
    // a duplicate delete after the roster failed to prune the row), which is a
    // success from the user's point of view — never surface the scary
    // "Failed to delete patient" error for it. Only genuine failures (network,
    // 5xx, permission) go down the error path.
    const succeed = () => {
      deleteDialog.onClose();
      toast({title: 'Patient deleted', status: 'success', duration: 2000});
      onDeleted?.(patient.id, patient);
    };
    try {
      await patientService.deletePatient(patient.id);
      succeed();
    } catch (err) {
      if (isDataLayerNotFound(err)) {
        succeed();
      } else {
        console.error('Failed to delete patient:', err);
        toast({
          title: 'Failed to delete patient',
          description: err?.message,
          status: 'error',
          duration: 4000,
        });
      }
    } finally {
      setDeleting(false);
    }
  }, [canDeletePatient, deleteDialog, onDeleted, patient, toast]);

  return {
    canDeletePatient,
    canEditPatient,
    cancelDeleteRef,
    closeDeleteDialog: deleteDialog.onClose,
    closeEditModal: editModal.onClose,
    deleteDialog,
    deleting,
    editModal,
    formData,
    handleDeletePatient,
    handleUpdatePatient,
    openDeleteDialog: deleteDialog.onOpen,
    openEditPatient,
    saving,
    setFormData,
  };
}
